import { memo, useEffect, useRef, useState } from 'react';
import { useTracking } from 'react-tracking';

import loadable from '@loadable/component';
import { RouterLink } from '@redux-first-router-link';

import { Box, Column, Grid } from '@simplywallst/ui-core';

import If from '@/components/If';
import { OneColumn } from '@/components/Layout';

import MobileTopbarSubNav, { ListItem } from '@/components/MobileTopbarSubNav';

import { ROUTE_DASHBOARD } from '@/constants/routes';

import {
  SUB_ROUTE_CALENDAR,
  SUB_ROUTE_SCREENERS,
  SUB_ROUTE_PERFORMANCE,
  SUB_ROUTE_UPDATES,
  updatesRoute,
  screenersRoute,
  performanceRoute,
  calendarRoute,
} from '../../../redux/constants';
import type { Entity } from '../../../redux/selectors/myMarketsAndIndustriesSelectors';

import { useFlag } from '@/features/unleash';
import useFeed from '../../../hooks/useFeed';
import useFeedSorting from '../../../hooks/useFeedSorting';
import useImpressionTracking from '../../../hooks/useImpressionTracking';

import Empty from '../../Empty';

import UpcomingEvents from '../../UpcomingEvents';

import DeprecatedPortfolioAndMyMarkets from '../../DeprecatedPortfolioAndMyMarkets';
import InternationalMarkets from '../../InternationalMarkets';
import MarketTopWorst from '../../MarketTopWorst';
import { SortingDropdownButton } from '../../SortingDropdownButton';

import { PortfoliosLoadingState } from '../../Portfolios/components/PortfoliosLoadingState';

import FeedCard, { FeedCardSkeleton } from '@/components/FeedCard';
import RecentArticles from '@/components/RecentArticles';
import { MyScreenersMobile as MyScreeners } from '@/components/Dashboard/components/MyScreeners';

const PortfoliosSection = loadable(() => import('../../Portfolios'), {
  resolveComponent: (c) => c.PortfoliosSection,
  fallback: <PortfoliosLoadingState />,
});

const Follow = loadable(() => import('../../Follow'));
const Footer = loadable(() => import('@/components/Footer'));
const BrazeContentCard = loadable(
  () => import('@/components/BrazeContentCard')
);

const MemoMarketTopWorst = memo(MarketTopWorst); // Until the Dashboard MVP is iterated on

export interface Props {
  isUserLoggedIn: boolean;
  subRoute?: string;
  isOnboardingSlideShowDismissed: boolean;
  myMarketKeys: string[];
  myIndustryKeys: string[];
  myMarketAndIndustryEntities: Record<string, Entity>;
  myMarketsAndIndustriesLastUpdated: number | null;
}

function nonNullable<T>(value: T): value is NonNullable<T> {
  return value !== null && value !== undefined;
}

export const DashboardMobile = ({
  isUserLoggedIn,
  subRoute,
  isOnboardingSlideShowDismissed,
  myMarketKeys,
  myIndustryKeys,
  myMarketAndIndustryEntities,
  myMarketsAndIndustriesLastUpdated,
}: Props) => {
  const [selected, setSelected] = useState(subRoute || SUB_ROUTE_UPDATES);

  const performanceTabRemovalFlag = useFlag({
    name: 'mono-market-performance-tab-removal',
  });
  const hasPerformanceRoute =
    performanceTabRemovalFlag.enabled === false &&
    performanceTabRemovalFlag.flagsReady === true;

  const myScreenersWidgetFlag = useFlag({ name: 'mono-my-screeners-widget' });
  const hasScreenersRoute =
    myScreenersWidgetFlag.enabled && myScreenersWidgetFlag.flagsReady;

  const routes = useRef(
    [
      updatesRoute,
      hasScreenersRoute ? screenersRoute : null,
      hasPerformanceRoute ? performanceRoute : null,
      calendarRoute,
    ].filter(nonNullable)
  );

  const { keys: feedKeys, entities, isFetched, isFetching } = useFeed();
  const { options, handleSelect, handleSorting, selectedSortType } =
    useFeedSorting(entities);

  const trackFeedFetch = useRef(false);

  const { onIntersection } = useImpressionTracking();

  const { trackEvent } = useTracking();

  useEffect(() => {
    if (isFetched && !trackFeedFetch.current) {
      const feedItemTypeCount = feedKeys.reduce((a, b) => {
        entities[b].feedItems.forEach(({ type }) => {
          const key = `${type}Count`;
          a[key] = a[key] ? a[key] + 1 : 1;
        });
        return a;
      }, {});
      trackEvent({
        type: 'track',
        action: 'feed_fetch_complete',
        feedLength: feedKeys.length,
        ...feedItemTypeCount,
      });
      trackFeedFetch.current = true;
    }
  }, [isFetched, trackFeedFetch, feedKeys, entities, trackEvent]);

  const isFeedEmpty = isFetched && feedKeys.length === 0;
  return (
    <OneColumn>
      <MobileTopbarSubNav
        routes={routes.current}
        selected={selected}
        onSelect={(_route, { subRoute }) => setSelected(subRoute)}
      >
        {({ routes, handleClick, refMap, selected }) =>
          routes.map(({ label, to, payload, tracking }, i) => (
            <ListItem
              ref={refMap[i]}
              key={`MobileTopbarListItem-${to}-${i}`}
              selected={selected === (payload || {}).subRoute}
            >
              <RouterLink
                inheritColor
                noUnderline
                to={{ type: to || ROUTE_DASHBOARD, payload }}
                onClick={() => handleClick(tracking || label, to, payload)}
                data-cy-id={`dashboard-mobile-nav-${
                  typeof payload === 'undefined' ? '' : payload.subRoute
                }`}
              >
                {label}
              </RouterLink>
            </ListItem>
          ))
        }
      </MobileTopbarSubNav>
      {selected === SUB_ROUTE_UPDATES && (
        <Column col={12} colLg={6}>
          <Box paddingRightLg="x4" paddingLeftLg="x4">
            <DeprecatedPortfolioAndMyMarkets
              myMarketKeys={myMarketKeys}
              myIndustryKeys={myIndustryKeys}
              myMarketAndIndustryEntities={myMarketAndIndustryEntities}
              myMarketsAndIndustriesLastUpdated={
                myMarketsAndIndustriesLastUpdated
              }
            />
            <Box as="section" marginTopLg="x2" marginBottomLg="x2">
              <Grid templateColumns="auto" templateRows="auto" gap="12px">
                {!isOnboardingSlideShowDismissed && <PortfoliosSection />}
                <RecentArticles />
              </Grid>
            </Box>
            <Box
              position="relative"
              zIndex={1}
              marginTop="x1"
              marginBottom="x1"
              marginTopLg="x0"
              marginBottomLg="x0"
            >
              <Grid
                templateColumns="max-content auto"
                alignItems="center"
                justifyContent="space-between"
              >
                {!isFeedEmpty && (
                  <Box
                    paddingTop="x1"
                    paddingBottom="x1"
                    paddingBottomLg="x2"
                    paddingRight="x2"
                    paddingLeft="x2"
                    paddingRightLg="x0"
                  >
                    <SortingDropdownButton
                      options={options}
                      handleSelect={handleSelect}
                      selectedSortType={selectedSortType}
                      disabled={!isFetched}
                    />
                  </Box>
                )}
              </Grid>
            </Box>
            {isUserLoggedIn && (
              <BrazeContentCard feedType="dashboard" anchorTarget="_blank" />
            )}
            {isFetching ? (
              <>
                <FeedCardSkeleton />
                <FeedCardSkeleton />
                <FeedCardSkeleton />
              </>
            ) : isFeedEmpty ? (
              <Empty />
            ) : (
              feedKeys
                .sort(handleSorting)
                .map((n, i) => (
                  <FeedCard
                    key={`feed-card-${n}`}
                    index={i}
                    company={entities[n]}
                    onIntersection={onIntersection}
                    data-cy-id="dashboard-feed-card"
                  />
                ))
            )}
            <If match={feedKeys.length < 5}>{() => <Follow />}</If>
            {RUNTIME_ENV !== 'native' && <Footer center={true} />}
          </Box>
        </Column>
      )}
      {hasScreenersRoute && selected === SUB_ROUTE_SCREENERS && (
        <Column col={12} colLg={3}>
          <Box as="section" padding="x2" paddingLg="x0">
            <MyScreeners />
          </Box>
        </Column>
      )}
      {selected === SUB_ROUTE_PERFORMANCE && (
        <Column col={12} colLg={3}>
          <Box padding="x2" paddingLg="x0">
            <Box as="section" marginBottom="x4">
              <MemoMarketTopWorst />
            </Box>
            <InternationalMarkets />
          </Box>
        </Column>
      )}
      {selected === SUB_ROUTE_CALENDAR && (
        <Column>
          <Box padding="x2" paddingLg="x0">
            <UpcomingEvents />
          </Box>
        </Column>
      )}
    </OneColumn>
  );
};
